import { useMemo, useState } from 'react';
import { Tags } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CatalogPlan,
  useCatalogQuery,
  useSetPlanActiveMutation,
  useSetPlanPriceMutation,
} from '@/client/api/payment/finance';
import { formatMoney } from '@/client/types/payment';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { ErrorNote, FinancePage, card, selectCls, td, th } from './shell';

type Edit = { plan: CatalogPlan; kind: 'price' | 'active' };

const CatalogPage = () => {
  const manage = useHasPermission('payment.manage');
  const canManage = manage.isSuperAdmin || manage.hasPermission;
  const [active, setActive] = useState<'' | '0' | '1'>('1');
  const [q, setQ] = useState('');
  const { data, isLoading, error } = useCatalogQuery({
    ...(active ? { active: Number(active) as 0 | 1 } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
  });

  const [edit, setEdit] = useState<Edit | null>(null);
  const [price, setPrice] = useState('');
  const [reason, setReason] = useState('');
  const priceMutation = useSetPlanPriceMutation();
  const activeMutation = useSetPlanActiveMutation();
  const saving = priceMutation.isPending || activeMutation.isPending;

  const groups = useMemo(() => {
    const out = new Map<string, CatalogPlan[]>();
    (data?.plans ?? []).forEach((p) => {
      const key = p.collection || 'Other';
      out.set(key, [...(out.get(key) ?? []), p]);
    });
    return [...out.entries()];
  }, [data]);

  const open = (plan: CatalogPlan, kind: Edit['kind']) => {
    setEdit({ plan, kind });
    setPrice(String(plan.prices[0]?.price ?? ''));
    setReason('');
  };

  const save = async () => {
    if (!edit) return;
    if (!reason.trim()) {
      toast.error('A reason is required', { richColors: true });
      return;
    }
    try {
      if (edit.kind === 'price') {
        await priceMutation.mutateAsync({
          toid: edit.plan.toid,
          price: Number(price),
          reason: reason.trim(),
          ccode: edit.plan.prices[0]?.ccode,
        });
        toast.success('Price updated', { richColors: true });
      } else {
        await activeMutation.mutateAsync({
          toid: edit.plan.toid,
          active: edit.plan.active ? 0 : 1,
          reason: reason.trim(),
        });
        toast.success(edit.plan.active ? 'Plan retired' : 'Plan reactivated', { richColors: true });
      }
      setEdit(null);
    } catch (e: any) {
      // The server names the nearest valid price when the convention is broken.
      toast.error(e?.message || 'Could not save', { richColors: true });
    }
  };

  return (
    <FinancePage
      icon={Tags}
      title="Plans & pricing"
      description="Every plan with its stored pre-tax price and what the buyer is billed. Prices are whole, end in 9, and must be at least twice the cost of the visits a plan dispatches; anything else is refused with the nearest valid price."
      actions={
        <div className="flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search plans"
            aria-label="Search plans"
            className="w-56"
          />
          <select
            aria-label="Plan state"
            className={selectCls}
            value={active}
            onChange={(e) => setActive(e.target.value as '' | '0' | '1')}
          >
            <option value="1">On sale</option>
            <option value="0">Retired</option>
            <option value="">All</option>
          </select>
        </div>
      }
    >
      <ErrorNote error={error} />
      {isLoading && <p className="text-sm text-gray-500">Loading the catalogue…</p>}
      {!isLoading && !groups.length && !error && (
        <p className="text-sm text-gray-500">No plans match.</p>
      )}
      {groups.map(([name, plans]) => (
        <div key={name} className={card}>
          <div className="border-b border-gray-100 px-5 py-3">
            <h2 className="text-sm font-semibold text-gray-800">
              {name} <span className="font-normal text-gray-500">· {plans.length}</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Plan', 'Days', 'Pre-tax', 'Buyer pays', 'Visit floor', 'State', ''].map((h, i) => (
                    <th key={i} className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {plans.map((p) => {
                  const pr = p.prices[0];
                  return (
                    <tr key={p.toid}>
                      <td className={td}>
                        <span className="font-medium text-gray-900">{p.name}</span>
                        <span className="block font-mono text-xs text-gray-500">
                          {p.ffid || `toid ${p.toid}`}
                          {p.tier ? ` · tier ${p.tier}` : ''}
                        </span>
                      </td>
                      <td className={`${td} tabular-nums`}>{p.sub_duration ?? '—'}</td>
                      <td className={`${td} font-semibold tabular-nums`}>
                        {pr?.price != null ? formatMoney(pr.price, pr.ccode) : 'No price'}
                        {pr?.rule_violation && (
                          <span className="block text-xs font-normal text-amber-700">
                            Breaks the pricing convention
                          </span>
                        )}
                      </td>
                      <td className={`${td} tabular-nums`}>
                        {pr?.price_incl_tax != null ? formatMoney(pr.price_incl_tax, pr.ccode) : '—'}
                      </td>
                      <td className={`${td} tabular-nums text-gray-500`}>
                        {p.price_floor != null ? formatMoney(p.price_floor, pr?.ccode) : '—'}
                      </td>
                      <td className={td}>
                        {p.active ? 'On sale' : <span className="text-gray-500">Retired</span>}
                      </td>
                      <td className={td}>
                        {canManage && (
                          <div className="flex justify-end gap-2">
                            {pr && (
                              <Button type="button" size="sm" variant="outline" onClick={() => open(p, 'price')}>
                                Change price
                              </Button>
                            )}
                            <Button type="button" size="sm" variant="outline" onClick={() => open(p, 'active')}>
                              {p.active ? 'Retire' : 'Reactivate'}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {edit.kind === 'price'
                    ? `Change price: ${edit.plan.name}`
                    : `${edit.plan.active ? 'Retire' : 'Reactivate'}: ${edit.plan.name}`}
                </DialogTitle>
                <DialogDescription>
                  {edit.kind === 'price'
                    ? `Enter the pre-tax price. Tax is added on the invoice.${
                        edit.plan.price_floor != null
                          ? ` This plan's visits set a minimum of ${formatMoney(edit.plan.price_floor, edit.plan.prices[0]?.ccode)}.`
                          : ''
                      } Existing subscriptions keep the amount they were sold at.`
                    : edit.plan.active
                      ? 'A retired plan can no longer be bought. Existing subscriptions are not affected.'
                      : 'The plan goes back on sale immediately.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {edit.kind === 'price' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="plan-price">Pre-tax price ({edit.plan.prices[0]?.ccode})</Label>
                    <Input
                      id="plan-price"
                      type="number"
                      min="0"
                      step="1"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="plan-reason">Reason</Label>
                  <Input
                    id="plan-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is this changing?"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEdit(null)}>
                  Cancel
                </Button>
                <Button type="button" disabled={saving} onClick={save}>
                  Save
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </FinancePage>
  );
};

export default CatalogPage;
